import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, QrCode } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import {
  fetchBookingPageSettings,
  updateBookingPageSettings,
} from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import {
  bookingContactUrl,
  bookingPageUrl,
  buildBookingPageQrSvg,
  svgToDataUrl,
  toUpdateRequest,
} from "../../features/trial-requests/utils/bookingPage";

/** Enable/disable the public booking page and share its indexable URL. */
const BookingPageSettingsCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchBookingPageSettings());
  }, [dispatch]);

  // Locale-less on purpose: the marketing site redirects /book/... to the
  // visitor's language, so the shared link works for everyone.
  const pageUrl = settings?.slug ? bookingPageUrl(settings.slug) : null;
  const contactUrl = settings?.slug ? bookingContactUrl(settings.slug) : null;
  const qrDataUrl = useMemo(
    () => (pageUrl ? svgToDataUrl(buildBookingPageQrSvg(pageUrl)) : null),
    [pageUrl],
  );

  const handleDownloadQr = () => {
    if (!qrDataUrl || !settings?.slug) return;
    // Rasterize the SVG at print resolution; fall back to the SVG itself.
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 1024;
      const context = canvas.getContext("2d");
      if (!context) return triggerDownload(qrDataUrl, `booking-page-${settings.slug}.svg`);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      triggerDownload(canvas.toDataURL("image/png"), `booking-page-${settings.slug}.png`);
    };
    image.onerror = () => triggerDownload(qrDataUrl, `booking-page-${settings.slug}.svg`);
    image.src = qrDataUrl;
  };

  const triggerDownload = (href: string, filename: string) => {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = filename;
    anchor.click();
  };

  const handleToggle = () => {
    if (!settings) return;
    // Round-trip everything so toggling the page never resets other settings.
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        enabled: !settings.enabled,
      }),
    );
  };

  const handleCopy = async (key: string, url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
            {t("trialRequests.bookingPageTitle", "Your public booking page")}
          </h2>
          <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "trialRequests.bookingPageIntro",
              "A public page where prospective members can request a trial lesson. When enabled, it is indexable by search engines.",
            )}
          </p>
        </div>
        <button
          type="button"
          className={settings?.enabled ? "btn btn-secondary shrink-0" : "btn btn-primary shrink-0"}
          onClick={handleToggle}
          disabled={!settings || status === "loading"}
        >
          {settings?.enabled
            ? t("trialRequests.disablePage", "Disable")
            : t("trialRequests.enablePage", "Enable")}
        </button>
      </div>

      {settings?.enabled && pageUrl && contactUrl && (
        <>
          {[
            { key: "page", label: t("trialRequests.pageLinkLabel", "Booking page"), url: pageUrl },
            { key: "contact", label: t("trialRequests.contactLinkLabel", "Contact form"), url: contactUrl },
          ].map(({ key, label, url }) => (
            <div key={key} className="mt-4">
              <p className="text-sm font-medium text-grey-20 dark:text-white">{label}</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-white">
                  {url}
                </code>
                <button type="button" className="btn btn-secondary" onClick={() => handleCopy(key, url)}>
                  <Copy size={16} />
                  {copiedKey === key
                    ? t("trialRequests.copied", "Copied!")
                    : t("trialRequests.copyLink", "Copy link")}
                </button>
              </div>
            </div>
          ))}
          {qrDataUrl && (
            <div className="mt-4 flex items-center gap-4">
              {/* QR codes need a light background to scan — the white chip stays in dark mode too. */}
              <img
                src={qrDataUrl}
                alt={t("trialRequests.qrAlt", "QR code linking to your booking page")}
                className="h-24 w-24 rounded-lg border border-grey-90 bg-white p-1 dark:border-grey-50"
              />
              <div>
                <button type="button" className="btn btn-secondary" onClick={handleDownloadQr}>
                  <QrCode size={16} />
                  {t("trialRequests.qrDownload", "Download QR code")}
                </button>
                <p className="mt-1 text-xs text-grey-40 dark:text-grey-80">
                  {t("trialRequests.qrHint", "For flyers, posters and the studio door — scans straight to your booking page.")}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {error && <p className="field-error mt-2">{error}</p>}
    </div>
  );
};

export default BookingPageSettingsCard;
